// "Transférer" — pick a discussion and re-send a message's text + attachments
// into it. Mirrors the hexagone thread's forward action.

import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMessagerieStore } from "@store/messagerieStore";
import type { ChatMessage } from "types/messaging";

export default function ForwardModal({
  message,
  currentDiscussionId,
  isDark,
  onClose,
}: {
  message: ChatMessage;
  currentDiscussionId?: string | null;
  isDark?: boolean;
  onClose: () => void;
}) {
  const discussions = useMessagerieStore((s) => s.discussions);
  const forwardMessage = useMessagerieStore((s) => s.forwardMessage);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return discussions
      .filter((d) => d.id !== currentDiscussionId)
      .filter((d) => !q || (d.name || "").toLowerCase().includes(q));
  }, [discussions, currentDiscussionId, search]);

  const forwardTo = async (targetId: string) => {
    if (busyId) return;
    setBusyId(targetId);
    try {
      await forwardMessage(targetId, message);
      onClose();
    } catch {
      setBusyId(null);
    }
  };

  const bg = isDark ? "#1F1F22" : "#FFFFFF";
  const border = isDark ? "#2E2E33" : "#F0F0F0";
  const text = isDark ? "#F5F5F5" : "#1E1E1E";
  const meta = isDark ? "#9CA3AF" : "#A1A1A1";
  const fieldBg = isDark ? "#2A2A2F" : "#F4F4F5";
  const hover = isDark ? "hover:bg-white/10" : "hover:bg-neutral-50";

  return createPortal(
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.35)" }}
      onClick={onClose}
    >
      <div
        className="flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 380, maxHeight: "70vh", background: bg,
          border: `1px solid ${border}`, borderRadius: 14,
          boxShadow: "0px 10px 32px rgba(0,0,0,0.2)", overflow: "hidden",
        }}
      >
        <div style={{ padding: "14px 16px 10px" }}>
          <div style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 15, color: text, marginBottom: 10 }}>
            Transférer vers…
          </div>
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une discussion"
            className="w-full outline-none"
            style={{
              fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: text,
              background: fieldBg, border: "1px solid transparent", borderRadius: 9,
              padding: "8px 11px",
            }}
          />
        </div>

        <div style={{ overflowY: "auto", padding: "0 8px 12px" }}>
          {list.length === 0 && (
            <div style={{ padding: 16, textAlign: "center", fontFamily: "Poppins", fontSize: 13, color: meta }}>
              Aucune discussion
            </div>
          )}
          {list.map((d) => (
            <button
              key={d.id}
              onClick={() => forwardTo(d.id)}
              disabled={!!busyId}
              className={`${hover} w-full flex flex-row items-center`}
              style={{
                gap: 10, padding: "10px 12px", borderRadius: 10,
                background: "transparent", border: "none", cursor: "pointer",
                textAlign: "left", opacity: busyId && busyId !== d.id ? 0.5 : 1,
              }}
            >
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {d.name || "Discussion"}
              </span>
              {busyId === d.id && (
                <span style={{ fontFamily: "Poppins", fontSize: 11, color: meta }}>Envoi…</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
